import { UserStore } from "@Front/Stores/UserStore";
import { LoggerService } from "@Front/Services/LoggerService/LoggerService";
import {
	assertAccessCookieForContext,
	getLeCoffreNotaryAccessTokenCookie,
	getLeCoffreNotaryRefreshTokenCookie,
} from "@Front/Utils/lecoffreSessionCookieHelpers";

const DEFAULT_RETRY_DELAY_MS = 120;
const DEFAULT_MAX_ATTEMPTS = 8;

function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Restores in-memory notary tokens from cookies after a full page reload (UserStore is empty until connect).
 */
export async function hydrateNotaryUserStoreFromCookiesIfPresent(): Promise<boolean> {
	if (UserStore.instance.isConnected()) {
		return true;
	}
	const accessToken = getLeCoffreNotaryAccessTokenCookie();
	const refreshToken = getLeCoffreNotaryRefreshTokenCookie();
	if (!accessToken || !refreshToken) {
		return false;
	}
	const connected = await UserStore.instance.connect(accessToken, refreshToken);
	if (connected) {
		LoggerService.info("ℹ️ [NotarySession] UserStore hydrated from notary cookies");
	}
	return connected;
}

/**
 * Ensures notary cookies and UserStore are ready before office switch or guest JWT alignment API calls.
 */
export async function ensureNotarySessionReadyForApi(
	maxAttempts: number = DEFAULT_MAX_ATTEMPTS,
	retryDelayMs: number = DEFAULT_RETRY_DELAY_MS,
): Promise<void> {
	let lastError: unknown;
	for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
		await hydrateNotaryUserStoreFromCookiesIfPresent();
		try {
			await assertAccessCookieForContext("notary", retryDelayMs);
			return;
		} catch (error: unknown) {
			lastError = error;
			if (attempt < maxAttempts - 1) {
				await sleep(retryDelayMs);
			}
		}
	}
	LoggerService.warn("⚠️ [NotarySession] Notary session not ready for API", { lastError });
	throw new Error("NOTARY_SESSION_NOT_READY");
}
